/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import Imgix from 'js/components/Imgix';

import { Grid, Typography, Typography2, breakpoints, useTheme } from '@coursera/cds-core';

import ProductTypePill from 'bundles/browse/components/Domain/ProductTypePill';
import type { GetBundleProductsQuery_XdpV1Resource_multiGet_elements as XdpProduct } from 'bundles/coursera-plus-landing-page/courseraPlusCollections/__generated__/GetBundleProductsQuery';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import { getS12nVariantLabel } from 'bundles/s12n-common/lib/s12nProductVariantUtils';
import ProductInfo from 'bundles/search-common/components/ProductInfo';
import { getSearchCardImageUrl } from 'bundles/search-common/utils/SearchUtils';

const useStyles = () => {
  const theme = useTheme();
  return {
    container: css`
      display: block;
      min-height: 150px;
      max-width: 1200px;
      width: 100%;
      border: 0;
      padding: 12px 0;
      padding-left: var(--cds-spacing-400);

      ${breakpoints.down('sm')} {
        padding-left: 0;
      }
    `,
    cardContent: css`
      background-color: white;
      min-height: 100px;
      display: flex;

      @media (max-width: 788px) {
        display: flex;
        flex-direction: row-reverse;
        justify-content: space-between;
        align-items: center;
      }

      @media (max-width: 425px) {
        flex-direction: column;
      }
    `,
    imageWrapper: css`
      height: 100%;
      width: 150px;
      margin-right: 20px;
      overflow: hidden;
      align-items: center;
      border: 1px solid #e1e1e1;
      box-shadow: 0 0 9px -1px #00000029;

      @media (max-width: 788px) {
        width: 65px;
        height: 65px;
        margin-right: 0;
      }
    `,
    productPhoto: css`
      width: 150px;
      height: 150px;

      @media (max-width: 788px) {
        width: 65px;
        height: 65px;
      }
    `,
    partnerName: css`
      font-size: 13px;
      color: #525252;
    `,
    cardTitle: css`
      font-size: 20px;
      font-weight: 600;
      margin-bottom: 0.5rem;
      display: inline-block;
    `,
    divider: css`
      border-bottom: 1px solid #efefef;
      margin-top: 24px;
      margin-bottom: 0;
    `,
    topDivider: css`
      border-top: 1px solid #efefef;
      margin-bottom: 24px;
      margin-top: 0;
    `,
  };
};

const capitalizeFirstLetter = (sentence: string) => {
  return sentence.charAt(0).toUpperCase() + sentence.slice(1).toLowerCase();
};

type ProductCardData = {
  id?: string | null;
  name?: string | null;
  partnerName: string;
  photoUrl?: string | null;
  enrollments: number | null;
  ratingCount?: number | null;
  averageFiveStarRating?: number | null;
  productDifficultyLevel?: string | null;
  productTypeName?: string | null;
};

const getFormattedProduct = (product: XdpProduct | null): ProductCardData | null => {
  const metadata = product?.xdpMetadata;
  if (metadata?.__typename === 'XdpV1_sdpMetadataMember') {
    return {
      id: product?.id,
      productTypeName: getS12nVariantLabel(metadata.sdpMetadata.productVariant),
      name: metadata?.sdpMetadata.name,
      partnerName: metadata.sdpMetadata.partners[0].name,
      photoUrl: metadata.sdpMetadata.certificateLogo,
      enrollments: metadata.sdpMetadata.totalEnrollmentCount,
      averageFiveStarRating: metadata.sdpMetadata.ratings?.averageFiveStarRating,
      ratingCount: metadata.sdpMetadata.ratings?.ratingCount,
      productDifficultyLevel: capitalizeFirstLetter(metadata.sdpMetadata.level as string),
    };
  } else if (metadata?.__typename === 'XdpV1_cdpMetadataMember') {
    return {
      id: product?.id,
      productTypeName: product?.productType,
      name: metadata?.cdpMetadata.name,
      partnerName: metadata.cdpMetadata.partners[0].name,
      photoUrl: metadata.cdpMetadata.photoUrl,
      enrollments: metadata.cdpMetadata.totalEnrollmentCount,
      averageFiveStarRating: metadata.cdpMetadata.ratings?.averageFiveStarRating,
      ratingCount: metadata.cdpMetadata.ratings?.ratingCount,
      productDifficultyLevel: capitalizeFirstLetter(metadata.cdpMetadata.level as string),
    };
  }

  return null;
};

type Props = {
  product: XdpProduct | null;
  index: number;
};

const CollectionProductCard: React.FC<Props> = ({ product, index }) => {
  const styles = useStyles();
  const formattedProduct = getFormattedProduct(product);
  const data = {
    productId: formattedProduct?.id,
  };

  if (!formattedProduct) {
    return null;
  }

  return (
    <TrackedDiv
      trackingName="collection_landing_product_card"
      trackClicks
      data={data}
      className="rc-CollectionProductCard"
      css={styles.container}
    >
      {index === 0 && <hr css={styles.topDivider} />}
      <Grid container css={styles.cardContent}>
        <Grid item css={styles.imageWrapper}>
          <Imgix
            src={getSearchCardImageUrl({ imageUrl: formattedProduct?.photoUrl })}
            alt={formattedProduct.name as string}
            maxWidth={150}
            maxHeight={150}
            imgParams={{ fit: 'fill', auto: 'format,compress', bg: 'FFF' }}
            css={styles.productPhoto}
          />
        </Grid>
        <Grid item>
          <Typography component="h2" variant="h2semibold" css={styles.cardTitle}>
            {formattedProduct?.name}
          </Typography>
          <Grid container>
            <Grid item>
              <Typography2 component="span" variant="bodySecondary" color="supportText" css={styles.partnerName}>
                {formattedProduct?.partnerName ?? ''}
              </Typography2>
            </Grid>
          </Grid>
          <Grid container>
            <Grid item>
              {formattedProduct?.productTypeName && (
                <ProductTypePill productTypeName={formattedProduct?.productTypeName} />
              )}
            </Grid>
          </Grid>
          <ProductInfo
            tagline={
              !formattedProduct?.enrollments &&
              !formattedProduct?.averageFiveStarRating &&
              !formattedProduct?.productDifficultyLevel
                ? 'Tagline'
                : undefined
            }
            enrollments={formattedProduct?.enrollments}
            avgProductRating={formattedProduct?.averageFiveStarRating}
            numProductRatings={formattedProduct?.ratingCount}
            productDifficultyLevel={formattedProduct?.productDifficultyLevel}
          />
        </Grid>
      </Grid>
      <hr css={styles.divider} />
    </TrackedDiv>
  );
};

export default CollectionProductCard;
